import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'

// ─── Types ────────────────────────────────────────────────────────────────────

type Phase = 'idle' | 'in' | 'out'

const BRAND_SHIELD = '/images/brand/burton-shield-gold.png'

interface TransitionContextValue {
  transitionTo: (to: string) => void
}

// ─── Context ──────────────────────────────────────────────────────────────────

const TransitionContext = createContext<TransitionContextValue | null>(null)

export function useTransition() {
  const ctx = useContext(TransitionContext)
  if (!ctx) throw new Error('useTransition must be used inside TransitionProvider')
  return ctx
}

// ─── Overlay ──────────────────────────────────────────────────────────────────

function TransitionOverlay({ phase }: { phase: Phase }) {
  if (phase === 'idle') return null

  return (
    <div
      className={`fixed inset-0 z-[9999] bg-carbon-950 flex items-center justify-center
                  ${phase === 'in' ? 'animate-fade-in' : 'animate-fade-out'}`}
    >
      <img
        src={BRAND_SHIELD}
        alt=""
        aria-hidden
        className="h-80 w-auto opacity-80"
      />
    </div>
  )
}

// ─── Provider ─────────────────────────────────────────────────────────────────

// Fade-in: 600ms  |  hold: 150ms  |  fade-out: 450ms
const CURTAIN_DURATION = 600
const HOLD_DURATION    = 150

export function TransitionProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>('idle')
  const navigate = useNavigate()
  // Guard against double-clicks during an active transition
  const busy = useRef(false)

  const transitionTo = useCallback((to: string) => {
    if (busy.current) return
    busy.current = true

    setPhase('in')

    setTimeout(() => {
      navigate(to)
      // Scroll to top on route change
      window.scrollTo(0, 0)

      setTimeout(() => {
        setPhase('out')

        setTimeout(() => {
          setPhase('idle')
          busy.current = false
        }, CURTAIN_DURATION)
      }, HOLD_DURATION)
    }, CURTAIN_DURATION)
  }, [navigate])

  return (
    <TransitionContext.Provider value={{ transitionTo }}>
      {children}
      <TransitionOverlay phase={phase} />
    </TransitionContext.Provider>
  )
}
